import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { captureException } from '@sentry/react';
import { ErrorComponentProps } from '@tanstack/react-router';

import { Button } from '@tg-web/components';
import { CircleIconWrapper, CowSkullIcon } from '@tg-web/icons';

import {
  isReloadingForStaleChunk,
  isStaleChunkError,
  reloadForStaleChunk,
} from '../lib/staleChunk';
import { useReportStaleChunkReload } from '../lib/useReportStaleChunkReload';
import { PageWrapper } from './PageWrapper';

export function ErrorPage({ error, reset }: ErrorComponentProps) {
  const { t } = useTranslation();
  // Read on the first render, not only in the effect, so a page that is already
  // being reloaded never draws the skull, not even for a frame.
  const [reloading, setReloading] = useState(isReloadingForStaleChunk);

  // A deploy that replaced this session's chunks is the most common way onto
  // this screen, and it is the one cause a reload fixes. Reported from here as
  // well as from `Root`, because a reload that came back to the same broken
  // build lands here and that branch is the one worth knowing about.
  const stale = isStaleChunkError(error);

  useReportStaleChunkReload();

  useEffect(() => {
    // Whatever failed while a reload is under way is the build being left
    // behind, whatever its message: the usual one is the TypeError a cancelled
    // `vite:preloadError` turns into (TELECAL-WEB-APP-15M). Reporting it filed
    // a Sentry error for a session the reload was about to recover, and it is
    // not `stale`, so it has to be checked for separately.
    //
    // StrictMode's double-invoked effect lands here too, and stays quiet
    // rather than reloading twice -- development only.
    if (isReloadingForStaleChunk() || (stale && reloadForStaleChunk())) {
      setReloading(true);

      return;
    }

    captureException(error, {});
  }, [error, stale]);

  if (reloading) {
    return null;
  }

  return (
    <PageWrapper className="flex flex-col items-center">
      <div className="grow" />
      <div className="flex shrink-0 flex-col items-center">
        <CircleIconWrapper className="mb-5">
          <CowSkullIcon />
        </CircleIconWrapper>
        <h1 className="typo-header-biggest mb-3">
          {t('all:error_page.title')}
        </h1>
        <p className="typo-text text-tg-subtitle-text mb-8 max-w-[280px] text-center">
          {t('all:error_page.text')}
        </p>
        {/*
          `reset` re-runs the route load, which is the right retry for a loader
          that failed and the wrong one for a missing chunk: a rejected dynamic
          import stays rejected in the browser's module map, so the same
          specifier fails again without a request and the button does visibly
          nothing. Reloading is the only thing that fetches a fresh
          `index.html` and with it the chunk names that exist. A person tapping
          is its own rate limit, so this one ignores the cap.
        */}
        <Button
          onClick={stale ? () => window.location.reload() : reset}
          size="small"
          variant="primary"
        >
          {t('all:common.buttons.try_again')}
        </Button>
      </div>
      <div className="grow" />
      <div className="grow" />
    </PageWrapper>
  );
}
